import { Popover, Stack, Text } from '@mantine/core';
import { KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import EditVersionContext from '@/context/EditVersionContext/EditVersionContext.tsx';
import useStaticDatasetUsage from '@/hooks/api/datasets/static/useStaticDatasetUsage.ts';
import { StaticDataset } from '@/types/datasets.ts';
import StaticDatasetUsageDetails from './StaticDatasetUsageDetails.tsx';

export default function StaticDatasetDependenciesPopover({ dataset }: { dataset: StaticDataset }) {
  const [opened, setOpened] = useState(false);
  const [pinned, setPinned] = useState(false);
  const target = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissing = useRef(false);
  const editor = useContext(EditVersionContext);
  const usage = useStaticDatasetUsage(dataset.id, false, { enabled: opened });
  const clearTimer = () => clearTimeout(timer.current);
  useEffect(() => () => clearTimeout(timer.current), []);
  const preview = () => {
    clearTimer();
    if (!dismissing.current) setOpened(true);
  };
  const leave = () => {
    clearTimer();
    timer.current = setTimeout(() => {
      if (
        !pinned &&
        !target.current?.contains(document.activeElement) &&
        !panel.current?.contains(document.activeElement)
      )
        setOpened(false);
    }, 250);
  };
  const dismiss = (returnFocus = false) => {
    clearTimer();
    setPinned(false);
    dismissing.current = true;
    setOpened(false);
    if (returnFocus) target.current?.querySelector('button')?.focus();
    queueMicrotask(() => {
      dismissing.current = false;
    });
  };
  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && opened) {
      event.stopPropagation();
      dismiss(true);
    }
  };
  return (
    <Popover
      opened={opened}
      onDismiss={() => dismiss()}
      closeOnEscape={false}
      clickOutsideCapture
      position="bottom-end"
      width={340}
      withinPortal
      trapFocus={pinned}
    >
      <Popover.Target>
        <span
          ref={target}
          onMouseEnter={preview}
          onMouseLeave={leave}
          onFocus={preview}
          onBlur={leave}
          onKeyDownCapture={handleEscape}
        >
          <StyledButton
            variant="subtleIcon"
            size="sm"
            aria-label={`Source dependencies for ${dataset.dataset.name}`}
            aria-expanded={opened}
            onClick={() => {
              if (pinned) dismiss();
              else {
                setPinned(true);
                setOpened(true);
              }
            }}
            styles={{
              root: { color: 'var(--mantine-color-gray-7)', backgroundColor: 'transparent' },
            }}
          >
            <HiOutlineInformationCircle size={16} />
          </StyledButton>
        </span>
      </Popover.Target>
      <Popover.Dropdown
        aria-label="Source dependencies"
        ref={panel}
        onMouseEnter={clearTimer}
        onMouseLeave={leave}
        onFocus={clearTimer}
        onBlur={leave}
        onKeyDownCapture={handleEscape}
        style={{ maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Source dependencies
          </Text>
          <StaticDatasetUsageDetails
            report={usage.data}
            item={usage.data?.results.find((entry) => entry.static_dataset_id === dataset.id)}
            loading={usage.isFetching}
            failed={usage.isError}
            paused={usage.fetchStatus === 'paused'}
            onRefresh={() => {
              void usage.refetch();
            }}
            allowDraftNavigation={
              !!editor?.editingVersion &&
              !editor.hasPendingEdits &&
              !editor.staleDraft &&
              editor.editingVersion.id === usage.data?.latest_version_id
            }
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
